import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import clsx from "clsx";
import { useT } from "../i18n";
import { Button } from "./Button";
import { Field, Input } from "./Field";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal dialog (§1.2: radius 16, elevation e3). Traps focus while open, restores it on close, closes on Escape
 * and on a click on the backdrop. `title` labels the dialog; `description` (optional) describes it.
 */
export function Dialog({ open, onClose, title, description, children, footer, size = "md", initialFocus }: {
  open: boolean; onClose: () => void; title: ReactNode; description?: ReactNode; children: ReactNode; footer?: ReactNode; size?: "sm" | "md" | "lg" | "xl";
  /** Selector of the element to focus first (default: first focusable in the body, else the close button). */
  initialFocus?: string;
}) {
  const t = useT();
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const restore = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restore.current = document.activeElement as HTMLElement | null;
    const node = panel.current;
    const first = (initialFocus && node?.querySelector<HTMLElement>(initialFocus)) || node?.querySelector<HTMLElement>(`[data-dialog-body] ${FOCUSABLE}`) || node?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); return; }
      if (e.key !== "Tab" || !node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (items.length === 0) { e.preventDefault(); return; }
      const firstEl = items[0]!, lastEl = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      else if (!node.contains(document.activeElement)) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      restore.current?.focus?.();
    };
  }, [open, onClose, initialFocus]);

  if (!open) return null;
  const sizes = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-[rgb(20_28_38/.45)] p-0 sm:items-start sm:p-10" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panel} className={clsx("flex max-h-full w-full flex-col rounded-t-dialog bg-surface shadow-e3 sm:rounded-dialog", sizes[size])} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={description ? `${id}-desc` : undefined}>
        <header className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
          <div className="min-w-0">
            <h2 id={`${id}-title`} className="text-h2">{title}</h2>
            {description && <p id={`${id}-desc`} className="mt-1 text-fg-2">{description}</p>}
          </div>
          <Button variant="ghost" size="sm" className="-mr-2 -mt-1 px-2" onClick={onClose} aria-label={t("ui.close")}><X className="h-4 w-4" aria-hidden="true" /></Button>
        </header>
        <div className="overflow-y-auto px-6 py-3" data-dialog-body>{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-2.5 px-6 pb-5 pt-3">{footer}</footer>}
      </div>
    </div>
  );
}

export type ConfirmLevel = "simple" | "type-to-confirm";

/**
 * Confirmation (§3). Undoable actions need no dialog (toast with Undo). Reversible but costly: `simple`,
 * naming the object and the count. Irreversible or tenant-wide: `type-to-confirm` – the user types `objectName`.
 * The cancel button says what stays (`keepLabel`, e.g. "Keep connector").
 * Legacy props `message` (= consequence) and `danger` keep working.
 */
export function ConfirmDialog({ open, onClose, onConfirm, title, message, consequence, level = "simple", objectName, confirmLabel, keepLabel, danger, loading }: {
  open: boolean; onClose: () => void; onConfirm: () => void; title: ReactNode; message?: ReactNode; consequence?: ReactNode; level?: ConfirmLevel; objectName?: string; confirmLabel?: string; keepLabel?: string; danger?: boolean; loading?: boolean;
}) {
  const t = useT();
  const [typed, setTyped] = useState("");
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  const needsName = level === "type-to-confirm" && !!objectName;
  const ok = !needsName || typed.trim() === objectName;
  return (
    <Dialog open={open} onClose={onClose} title={title} size="sm" initialFocus={needsName ? "input" : undefined} footer={<>
      <Button variant="secondary" onClick={onClose}>{keepLabel ?? t("ui.cancel")}</Button>
      <Button variant={danger || needsName ? "danger" : "primary"} onClick={onConfirm} disabled={!ok} loading={loading}>{confirmLabel ?? t("ui.confirm")}</Button>
    </>}>
      <div className="flex flex-col gap-4 text-fg-2">
        <div>{consequence ?? message}</div>
        {needsName && (
          <Field label={t("ui.typeToConfirm", { name: objectName })}>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
